import type { CSSProperties, ReactNode } from 'react';
import type { Node } from 'reactflow';
import type { TopologyV2Edgeschema, TopologyV2Nodeschema } from '@app/types/sdk';
import { topologyPresentationString } from './presentation';

type TopologyNode = TopologyV2Nodeschema;
type TopologyEdge = TopologyV2Edgeschema;

export type PortSide = 'top' | 'right' | 'bottom' | 'left';
export type DeviceBox = { x: number; y: number; w: number; h: number };

export type EdgeChipNodeData = {
  label: ReactNode;
  text: string;
  ui: Record<string, unknown>;
  side?: PortSide;
  overlayKey?: string;
};

export const EDGE_CHIP_NODE_SIZE = 42;
const EDGE_CHIP_GAP = 18;
const EDGE_CHIP_NODE_STYLE: CSSProperties = {
  width: EDGE_CHIP_NODE_SIZE,
  height: EDGE_CHIP_NODE_SIZE,
  borderRadius: 999,
  border: '2px solid rgba(76, 29, 149, 0.72)',
  background: 'rgba(255,255,255,0.98)',
  boxShadow: '0 2px 8px rgba(15,23,42,0.16), inset 0 0 0 7px rgba(99,102,241,0.08)',
  overflow: 'visible',
  zIndex: 5
};

const getNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

export const isEdgeChipRender = (ui: Record<string, unknown> | undefined): boolean => {
  const renderAs = ui?.renderAs;
  return typeof renderAs === 'string' && renderAs.trim().toLowerCase() === 'edge_chip';
};

export const topAncestorByNodeId = (id: string, embedParents: Map<string, string>): string => {
  let current = id;
  const seen = new Set<string>();
  while (embedParents.has(current) && !seen.has(current)) {
    seen.add(current);
    current = embedParents.get(current) ?? current;
  }
  return current;
};

export const pickSideByBoxes = (parent: DeviceBox, other: DeviceBox): PortSide => {
  const dx = other.x - parent.x;
  const dy = other.y - parent.y;

  const nx = Math.abs(dx) / Math.max(1, parent.w / 2);
  const ny = Math.abs(dy) / Math.max(1, parent.h / 2);

  if (nx >= ny) {
    return dx >= 0 ? 'right' : 'left';
  }
  return dy >= 0 ? 'bottom' : 'top';
};

export const absoluteNodeOrigin = (
  node: Node<EdgeChipNodeData>,
  nodeById: Map<string, Node<EdgeChipNodeData>>
): { x: number; y: number } => {
  let x = node.position.x;
  let y = node.position.y;
  let parentId = node.parentNode;
  const seen = new Set<string>();
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = nodeById.get(parentId);
    if (!parent) {
      break;
    }
    x += parent.position.x;
    y += parent.position.y;
    parentId = parent.parentNode;
  }
  return { x, y };
};

export const diagramNodeDimensions = (
  node: Node<EdgeChipNodeData>,
  deviceDimensions: Map<string, { width: number; height: number }>
): { width: number; height: number } => {
  const style = node.style as Record<string, unknown> | undefined;
  return {
    width: getNumber(style?.width) ?? deviceDimensions.get(node.id)?.width ?? EDGE_CHIP_NODE_SIZE,
    height: getNumber(style?.height) ?? deviceDimensions.get(node.id)?.height ?? EDGE_CHIP_NODE_SIZE
  };
};

const edgeChipNodeLabel = (text: string): ReactNode => (
  <div
    title={text}
    style={{
      width: '100%',
      height: '100%',
      borderRadius: 999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 4,
      overflow: 'hidden',
      textAlign: 'center',
      color: '#111827',
      fontSize: 8,
      fontWeight: 800,
      lineHeight: 1.05,
      wordBreak: 'break-word'
    }}
  >
    {text}
  </div>
);

export const buildEdgeChipNodes = <TData extends EdgeChipNodeData>(input: {
  edgeChipNodes: TopologyNode[];
  nodeById: Map<string, TopologyNode>;
  embedParents: Map<string, string>;
  childrenByParent: Map<string, string[]>;
  edgesByNode: Map<string, TopologyEdge[]>;
  topNodeBoxes: Map<string, DeviceBox>;
  deviceDimensions: Map<string, { width: number; height: number }>;
  nodeUIById: Map<string, Record<string, unknown>>;
  overlayKeyByNodeId: Record<string, string | undefined>;
  nodeOverlayStyle: (properties?: Record<string, unknown>) => CSSProperties;
}): Node<TData>[] => {
  const {
    edgeChipNodes,
    nodeById,
    embedParents,
    childrenByParent,
    edgesByNode,
    topNodeBoxes,
    deviceDimensions,
    nodeUIById,
    overlayKeyByNodeId,
    nodeOverlayStyle
  } = input;

  const peerTopNodeForChip = (chipId: string): string | undefined => {
    const ownTop = topAncestorByNodeId(chipId, embedParents);
    for (const edge of edgesByNode.get(chipId) ?? []) {
      if (edge.labels?.relation !== 'connects') {
        continue;
      }
      const otherId = edge.fromId === chipId ? edge.toId : edge.fromId;
      const otherTop = topAncestorByNodeId(otherId, embedParents);
      if (otherTop && otherTop !== ownTop && topNodeBoxes.has(otherTop)) {
        return otherTop;
      }
    }
    const endpoints = childrenByParent.get(chipId) ?? [];
    for (const endpointId of endpoints) {
      for (const edge of edgesByNode.get(endpointId) ?? []) {
        if (edge.labels?.relation !== 'connects') {
          continue;
        }
        const otherId = edge.fromId === endpointId ? edge.toId : edge.fromId;
        const otherTop = topAncestorByNodeId(otherId, embedParents);
        if (otherTop && otherTop !== ownTop && topNodeBoxes.has(otherTop)) {
          return otherTop;
        }
      }
    }
    return undefined;
  };

  const chipPlacementGroups = new Map<string, { siteId: string; side: PortSide; chipIds: string[] }>();
  edgeChipNodes.forEach((chip) => {
    const parentId = embedParents.get(chip.id);
    const siteId = parentId ? topAncestorByNodeId(parentId, embedParents) : '';
    const siteBox = topNodeBoxes.get(siteId);
    if (!siteId || !siteBox) {
      return;
    }
    const peerId = peerTopNodeForChip(chip.id);
    const side = peerId && topNodeBoxes.has(peerId) ? pickSideByBoxes(siteBox, topNodeBoxes.get(peerId)!) : 'bottom';
    const key = `${siteId}::${side}`;
    const group = chipPlacementGroups.get(key) ?? { siteId, side, chipIds: [] };
    group.chipIds.push(chip.id);
    chipPlacementGroups.set(key, group);
  });

  const output: Node<TData>[] = [];
  const addEdgeChipNode = (chipId: string, parentNode: string, position: { x: number; y: number }) => {
    const chipNode = nodeById.get(chipId);
    const text = topologyPresentationString(chipNode?.properties, 'title', chipNode?.displayName ?? chipId);
    const ui = nodeUIById.get(chipId) ?? {};
    output.push({
      id: chipId,
      parentNode,
      position,
      draggable: false,
      selectable: true,
      data: {
        text,
        label: edgeChipNodeLabel(text),
        ui,
        overlayKey: overlayKeyByNodeId[chipId]
      } as TData,
      className: 'topology-v2__node--edge-chip',
      style: {
        ...EDGE_CHIP_NODE_STYLE,
        ...nodeOverlayStyle(chipNode?.properties)
      }
    });
  };

  chipPlacementGroups.forEach((group) => {
    const siteDims = deviceDimensions.get(group.siteId) ?? { width: 0, height: 0 };
    const ordered = [...group.chipIds].sort((a, b) => {
      const aText = topologyPresentationString(nodeById.get(a)?.properties, 'title', nodeById.get(a)?.displayName ?? a);
      const bText = topologyPresentationString(nodeById.get(b)?.properties, 'title', nodeById.get(b)?.displayName ?? b);
      return aText.localeCompare(bText);
    });
    if (group.side === 'top' || group.side === 'bottom') {
      const totalWidth = ordered.length * EDGE_CHIP_NODE_SIZE + Math.max(0, ordered.length - 1) * EDGE_CHIP_GAP;
      let cursor = (siteDims.width - totalWidth) / 2;
      ordered.forEach((chipId) => {
        const x = cursor;
        const y = group.side === 'top' ? -EDGE_CHIP_NODE_SIZE / 2 : siteDims.height - EDGE_CHIP_NODE_SIZE / 2;
        cursor += EDGE_CHIP_NODE_SIZE + EDGE_CHIP_GAP;
        addEdgeChipNode(chipId, group.siteId, { x, y });
      });
      return;
    }

    const totalHeight = ordered.length * EDGE_CHIP_NODE_SIZE + Math.max(0, ordered.length - 1) * EDGE_CHIP_GAP;
    let cursor = (siteDims.height - totalHeight) / 2;
    ordered.forEach((chipId) => {
      const x = group.side === 'left' ? -EDGE_CHIP_NODE_SIZE / 2 : siteDims.width - EDGE_CHIP_NODE_SIZE / 2;
      const y = cursor;
      cursor += EDGE_CHIP_NODE_SIZE + EDGE_CHIP_GAP;
      addEdgeChipNode(chipId, group.siteId, { x, y });
    });
  });

  return output;
};
